/**
 * Ambient traffic.
 *
 * The point is the illusion of life, not a traffic simulator: cars drive the
 * lane graph, slow for the car in front, and give way at junctions. Nothing
 * here models lane changes, indicators, or drivers with anywhere to be.
 *
 * Two decisions carry most of the cost:
 *
 * - **Handling is `stepCar`**, the same integrator the player's vehicle uses
 *   (ADR-0011). Traffic that moved by a different rule would need its own
 *   tuning and its own bugs, and would visibly not match the car the player
 *   is driving alongside.
 * - **Rendering is two `InstancedMesh` draws for the whole fleet**, not a
 *   group per car. Twenty cars built the way the parked ones are would be
 *   eighty-odd draw calls against a budget that already has 727 of 900 spent.
 *   Instancing makes the fleet cost the same as two cars.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { standard } from './Materials.js';
import { buildRoadNetwork, type RoadNetwork, type Lane } from './RoadNetwork.js';
import {
  createCarState, stepCar, angleDelta, clamp, TRAFFIC_CAR, type CarState,
} from './CarDynamics.js';

/** Hard ceiling. The brief asks for 8-20; this is the 20. */
export const MAX_TRAFFIC = 20;

/** How close a car gets to the end of its lane before picking the next one. */
const ARRIVE_DISTANCE = 3.2;
/** Look-ahead for the car in front, before the speed-dependent part. */
const FOLLOW_BASE = 7;
/** A car within this of a junction is claiming it. */
const JUNCTION_REACH = 9;

interface TrafficAgent {
  car: CarState;
  lane: Lane;
  active: boolean;
  /** Body colour index, so a respawned car is not always the same one. */
  tint: number;
  /** Node this car has claimed, or -1. */
  claimed: number;
  /** Seconds alive, used to retire cars that get wedged. */
  age: number;
}

export interface TrafficSystem {
  group: THREE.Group;
  /**
   * @param hour 0..24, drives density
   * @param playerX where the player is, so cars spawn out of sight
   * @param playerCar the player's vehicle when they are driving, so traffic
   *   brakes for it. Without this a car will drive straight through the one
   *   the player is sitting in, which is the single most obvious bug this
   *   system could ship with.
   */
  update(
    dt: number, hour: number, playerX: number, playerZ: number,
    playerCar?: { x: number; z: number; heading: number } | null,
  ): void;
  /** Cap the fleet, for the quality setting. */
  setBudget(fraction: number): void;
  /** Live count, for the performance report and tests. */
  readonly activeCount: number;
  /** Positions of moving cars, so the player's collision can see them. */
  obstacles(): { x: number; z: number }[];
}

const BODY_COLOURS = [
  0x9a3f38, 0x2f4f6b, 0x6b6f74, 0x8a8578, 0x3f5f4a,
  0xb08a3f, 0x54566b, 0xa8a49c, 0x35424a, 0x7a4636,
];

export function createTraffic(network: RoadNetwork = buildRoadNetwork()): TrafficSystem {
  const group = new THREE.Group();

  // --- geometry -----------------------------------------------------------
  // One merged body and one merged set of dark parts, so the whole fleet is
  // two draw calls regardless of how many cars are moving.
  const bodyGeometry = mergeGeometries([
    box(1.78, 0.62, 4.0, 0, 0.74, 0),
    box(1.66, 0.66, 2.1, 0, 1.34, -0.2),
  ], false)!;
  const trimGeometry = mergeGeometries([
    // Wheels as squat boxes rather than cylinders: at the distance traffic is
    // ever seen, the silhouette is identical and the triangle count is a
    // fraction. Twenty cars is not the place to spend geometry on tread.
    box(0.26, 0.62, 0.62, -0.9, 0.33, 1.25),
    box(0.26, 0.62, 0.62, 0.9, 0.33, 1.25),
    box(0.26, 0.62, 0.62, -0.9, 0.33, -1.25),
    box(0.26, 0.62, 0.62, 0.9, 0.33, -1.25),
    box(1.52, 0.44, 0.07, 0, 1.38, 0.86),
    box(1.52, 0.42, 0.07, 0, 1.38, -1.26),
  ], false)!;

  const bodyMesh = new THREE.InstancedMesh(
    bodyGeometry,
    standard(0xffffff, { rough: 0.55, metal: 0.2 }),
    MAX_TRAFFIC,
  );
  const trimMesh = new THREE.InstancedMesh(
    trimGeometry,
    standard(0x1b1e24, { rough: 0.85 }),
    MAX_TRAFFIC,
  );
  for (const mesh of [bodyMesh, trimMesh]) {
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    // Every instance starts parked at the origin; without a frustum culling
    // opt-out three would cull the whole mesh whenever that origin is off
    // screen, and the entire fleet would blink out at once.
    mesh.frustumCulled = false;
    group.add(mesh);
  }
  bodyMesh.instanceColor = new THREE.InstancedBufferAttribute(
    new Float32Array(MAX_TRAFFIC * 3), 3,
  );

  const agents: TrafficAgent[] = [];
  for (let i = 0; i < MAX_TRAFFIC; i++) {
    agents.push({
      car: createCarState(0, 0, 0),
      lane: network.lanes[0]!,
      active: false,
      tint: i % BODY_COLOURS.length,
      claimed: -1,
      age: 0,
    });
  }

  // Node claims: one car may be in a junction at a time. This is the whole of
  // the give-way rule, and it is enough - a car that arrives second waits,
  // which from outside reads exactly like yielding.
  const claims = new Map<number, number>();
  let budget = 1;

  const matrix = new THREE.Matrix4();
  const quat = new THREE.Quaternion();
  const scale = new THREE.Vector3(1, 1, 1);
  const position = new THREE.Vector3();
  const colour = new THREE.Color();

  /**
   * How many cars should be on the road at this hour.
   *
   * Two rush hours and a near-empty night. The shape matters more than the
   * numbers: a street with constant traffic at 04:00 reads as a screensaver.
   */
  function densityAt(hour: number): number {
    if (hour < 5) return 0.1;
    if (hour < 7) return 0.35;
    if (hour < 9.5) return 1;
    if (hour < 16) return 0.6;
    if (hour < 18.5) return 1;
    if (hour < 21) return 0.55;
    return 0.25;
  }

  function laneAwayFrom(x: number, z: number): Lane | null {
    // Spawn out of the player's immediate view. A car appearing thirty metres
    // ahead is worse than no car at all.
    for (let attempt = 0; attempt < 24; attempt++) {
      const lane = network.lanes[Math.floor(Math.random() * network.lanes.length)]!;
      if (lane.length < 12) continue;
      const d = Math.hypot(lane.x1 - x, lane.z1 - z);
      if (d > 55 && d < 190) return lane;
    }
    return null;
  }

  function spawn(agent: TrafficAgent, playerX: number, playerZ: number): void {
    const lane = laneAwayFrom(playerX, playerZ);
    if (!lane) return;
    agent.lane = lane;
    agent.car.x = lane.x1;
    agent.car.z = lane.z1;
    agent.car.heading = lane.heading;
    agent.car.speed = TRAFFIC_CAR.maxSpeed * 0.6;
    agent.car.steer = 0;
    agent.tint = Math.floor(Math.random() * BODY_COLOURS.length);
    agent.claimed = -1;
    agent.age = 0;
    agent.active = true;
  }

  function retire(agent: TrafficAgent): void {
    agent.active = false;
    if (agent.claimed >= 0) {
      claims.delete(agent.claimed);
      agent.claimed = -1;
    }
  }

  function update(
    dt: number, hour: number, playerX: number, playerZ: number,
    playerCar: { x: number; z: number; heading: number } | null = null,
  ): void {
    const wanted = Math.round(MAX_TRAFFIC * budget * densityAt(hour));

    let live = 0;
    for (const a of agents) if (a.active) live++;

    for (const agent of agents) {
      if (!agent.active) {
        if (live < wanted) {
          spawn(agent, playerX, playerZ);
          if (agent.active) live++;
        }
        continue;
      }

      agent.age += dt;
      const target = { x: agent.lane.x2, z: agent.lane.z2 };
      const toEnd = Math.hypot(target.x - agent.car.x, target.z - agent.car.z);

      // --- pick the next lane ---------------------------------------------
      if (toEnd < ARRIVE_DISTANCE) {
        const node = network.nodes[agent.lane.toNode]!;
        const options = node.outgoing.filter((id) => {
          const next = network.lanes[id]!;
          // No U-turns: a lane whose heading is roughly the reverse of this
          // one is the same street going back, and cars flipping round in the
          // middle of a junction looks like a bug even when it is legal.
          return Math.abs(angleDelta(agent.lane.heading, next.heading)) < 2.4;
        });
        if (options.length === 0) {
          retire(agent);
          continue;
        }
        agent.lane = network.lanes[options[Math.floor(Math.random() * options.length)]!]!;
        if (agent.claimed >= 0) {
          claims.delete(agent.claimed);
          agent.claimed = -1;
        }
      }

      // --- give way --------------------------------------------------------
      let yielding = false;
      const node = network.nodes[agent.lane.toNode]!;
      if (node.junction) {
        const distance = Math.hypot(node.x - agent.car.x, node.z - agent.car.z);
        if (distance < JUNCTION_REACH) {
          const holder = claims.get(node.id);
          if (holder === undefined) {
            claims.set(node.id, agents.indexOf(agent));
            agent.claimed = node.id;
          } else if (holder !== agents.indexOf(agent)) {
            yielding = true;
          }
        }
      }

      // --- follow the car in front -----------------------------------------
      const forwardX = Math.sin(agent.car.heading);
      const forwardZ = Math.cos(agent.car.heading);
      const reach = FOLLOW_BASE + Math.abs(agent.car.speed) * 0.9;
      let blocked = false;

      // The player's car counts as traffic. It is checked with a wider cone
      // and a longer reach than another agent, because the player will do
      // things no agent does - stop across a lane, reverse out of a driveway,
      // sit still in the middle of a junction - and traffic that only yields
      // to well-behaved cars would drive through all of it.
      if (playerCar) {
        const dx = playerCar.x - agent.car.x;
        const dz = playerCar.z - agent.car.z;
        const distance = Math.hypot(dx, dz);
        if (distance < reach + 4 && distance > 0.001) {
          const ahead = (dx * forwardX + dz * forwardZ) / distance;
          if (ahead > 0.35) blocked = true;
        }
      }

      for (const other of agents) {
        if (other === agent || !other.active) continue;
        if (blocked) break;
        const dx = other.car.x - agent.car.x;
        const dz = other.car.z - agent.car.z;
        const distance = Math.hypot(dx, dz);
        if (distance > reach || distance < 0.001) continue;
        // Ahead, and roughly in the same direction of travel: a car crossing
        // at ninety degrees is the junction rule's problem, not the
        // car-following rule's, and braking for it here would gridlock every
        // crossroads in the neighbourhood.
        const ahead = (dx * forwardX + dz * forwardZ) / distance;
        if (ahead < 0.72) continue;
        if (Math.abs(angleDelta(agent.car.heading, other.car.heading)) > 1.2) continue;
        blocked = true;
        break;
      }

      // --- steer and drive --------------------------------------------------
      const desired = Math.atan2(target.x - agent.car.x, target.z - agent.car.z);
      const error = angleDelta(agent.car.heading, desired);
      const steer = clamp(error * 1.9, -1, 1);
      const stop = blocked || yielding;

      const next = stepCar(agent.car, {
        throttle: stop ? -1 : 1,
        steer,
        handbrake: false,
      }, TRAFFIC_CAR, dt);
      agent.car.x = next.x;
      agent.car.z = next.z;

      // Retire anything that has wandered far from the player or has been
      // stuck long enough to suggest it is wedged. Both are cheaper than a
      // recovery behaviour, and neither is visible: the car is off screen.
      const away = Math.hypot(agent.car.x - playerX, agent.car.z - playerZ);
      if (away > 210 || (agent.age > 25 && Math.abs(agent.car.speed) < 0.2)) {
        retire(agent);
        continue;
      }
      if (live > wanted && away > 120) {
        retire(agent);
        live--;
      }
    }

    // --- write transforms ---------------------------------------------------
    let index = 0;
    for (const agent of agents) {
      if (!agent.active) continue;
      position.set(agent.car.x, 0, agent.car.z);
      quat.setFromAxisAngle(UP, agent.car.heading);
      matrix.compose(position, quat, scale);
      bodyMesh.setMatrixAt(index, matrix);
      trimMesh.setMatrixAt(index, matrix);
      colour.setHex(BODY_COLOURS[agent.tint]!);
      bodyMesh.setColorAt(index, colour);
      index++;
    }
    // `count` is what stops retired instances from being drawn at wherever
    // they died; the alternative is scaling them to zero, which still costs
    // vertex work for every one of them.
    bodyMesh.count = index;
    trimMesh.count = index;
    bodyMesh.instanceMatrix.needsUpdate = true;
    trimMesh.instanceMatrix.needsUpdate = true;
    if (bodyMesh.instanceColor) bodyMesh.instanceColor.needsUpdate = true;
  }

  return {
    group,
    update,
    setBudget(fraction: number): void {
      budget = clamp(fraction, 0, 1);
    },
    get activeCount() {
      return agents.reduce((n, a) => n + (a.active ? 1 : 0), 0);
    },
    obstacles(): { x: number; z: number }[] {
      const out: { x: number; z: number }[] = [];
      for (const a of agents) if (a.active) out.push({ x: a.car.x, z: a.car.z });
      return out;
    },
  };
}

const UP = new THREE.Vector3(0, 1, 0);

function box(w: number, h: number, d: number, x: number, y: number, z: number) {
  const g = new THREE.BoxGeometry(w, h, d);
  g.translate(x, y, z);
  return g;
}
